// ============================================================================
// EFECTO GENIO — el panel NACE desde la tarjeta y VUELVE a ella (T-LAT-3, R-LAT-01).
// Lo pidió Cristhian con estas palabras: «efecto tipo rebote, tipo aladino, como
// los de Mac», «muy fino, muy delicado», «con un rebote cuando termine de abrir,
// pero sutil». Y el 2-sep-2026, mirándolo ya en producción: «se ve tosco… tiene
// que ser suave y SIMÉTRICO… como se ven las ventanas de macOS cuando se
// minimiza… también tiene que ir la animación justo sobre la tarjeta».
//
// ⛔⛔ POR QUÉ NO SE DEFORMA EL PANEL (2-sep-2026, segunda versión):
// La primera versión estiraba el panel de verdad: `scale(0.28, 0.13)` + `skewY`.
// Eso deforma TODO lo de dentro —letras aplastadas 10 veces y estiradas de golpe—
// y por eso se veía tosco. macOS no deforma una ventana viva: deforma una IMAGEN.
// Aquí se hace lo mismo con una **lámina fantasma** del color del panel a la que
// se le anima el `transform` (desde el 2-nov; antes `clip-path`, ver `forma()`):
// la geometría viaja de la tarjeta al panel y el panel de verdad solo hace un
// fundido encima. El texto nunca se deforma porque el panel nunca se ESCALA.
// ⚠️ Desde T-LAT-7 el panel SÍ lleva un `transform`, y no contradice lo de
//    arriba: es un `translateX` de 7 px (el rebote como bloque, ver ↓). Trasladar
//    no deforma una letra; escalar sí. La prohibición es sobre `scale`/`skew`.
//
// ⭐⭐ T-LAT-7 · UNA SOLA CURVA PARA LOS CUATRO BORDES (2-sep-2026).
// Antes cada borde llevaba SU muelle (el «aladino»: el derecho salía disparado,
// el izquierdo llegaba tarde y rebotaba). Filmado en un Chromium real, eso daba
// dos defectos que Cristhian vio a ojo:
//   · el gesto se AGOTABA a los ~180 ms de los 500 declarados (del 180 al 500 el
//     borde izquierdo se movía 4 px): un golpe seco con una cola muerta;
//   · a los 60 ms la lámina ya no estaba sobre la tarjeta — medía 769×237 y
//     flotaba lisa en mitad de la pantalla. Era lo único que el ojo veía.
// Ahora hay UNA curva, la misma para izquierda, derecha, arriba y abajo, y va
// calibrada CONTRA LA VARA (V-227), no de memoria.
//
// ⛔⛔ POR QUÉ LA CURVA NO ES UN MUELLE CRÍTICO A SECAS, Y ESTÁ MEDIDO:
// un `resorte(t, R, 1)` puro NO PUEDE cumplir V-227. Esa vara exige, a la vez,
// «a la mitad del tiempo el avance está entre 0,40 y 0,65» y «al 90 % del tiempo
// ya va por ≥ 0,97». Con la fórmula crítica `1-(1+x)e^-x` eso pide
// w₀·(dur/2) ≤ 2,22 **y** w₀·(0,9·dur) ≥ 5,36 — o sea w₀ ≤ 8,9 y w₀ ≥ 11,9 a la
// vez. Imposible: un muelle crítico es front-loaded por construcción, que es
// EXACTAMENTE el defecto que este tramo viene a quitar.
// ⇒ La curva es ese mismo muelle crítico **evaluado en tiempo suavizado**
//   (`smoothstep`, que le quita el arranque de golpe) y **normalizado** para
//   aterrizar exacto en 1. Sigue siendo física de muelle —una sola constante,
//   `TENSION`— y ahora reparte el recorrido a lo largo de toda la duración.
//
// ⛔⛔ EL REBOTE YA NO ES DEL RECORTE: ES DEL PANEL ENTERO (T-LAT-7).
// Cristhian, mirándolo: «solo rebota una parte del cuadro del chat, como la
// parte central… el borde de arriba y el de abajo se mantiene estático y da la
// sensación de que está un poco roto». La causa medida son dos capas: (a) sólo
// el borde izquierdo llevaba muelle con rebote, y (b) aunque rebotaran los
// cuatro, TRES NO SE PUEDEN VER — el panel llega pegado a `y=0`, `y=900` y
// `x=1440`, o sea a los bordes de la ventana. Sólo el izquierdo es visible.
// ⇒ El recorte llega CRÍTICO, sin rebote propio, y el rebote pasa a ser un
//   `translateX` de 7 px sobre **panel y lámina a la vez**: todo el material
//   llega y se asienta junto, que es lo de macOS. Y `translateX` no deforma.
//
// ⛔⛔ I-10, LAS DOS MITADES, Y NINGUNA ES DECORATIVA:
//  a) **Ninguna animación IGNORA `prefers-reduced-motion`.** Quien pidió no
//     moverse no se marea por un adorno nuestro. Aquí se comprueba a mano —y no
//     solo en el CSS— porque esto anima por JS (`element.animate`) y la regla
//     `* { animation: none !important }` de `estilos.css` NO alcanza a las
//     animaciones de la Web Animations API.
//  b) **Ninguna animación BLOQUEA la acción.** Esta función siempre devuelve una
//     promesa que RESUELVE: sin `desde`, sin `el.animate` (jsdom no implementa
//     Web Animations), con reduced-motion, o si la animación se cancela a medias.
//     Un `await` sobre una promesa que nunca llega deja el panel a medio abrir y
//     al asesor mirando una pantalla muerta. **El temblor de la tarjeta tampoco
//     bloquea**: es un adorno colgado de un `setTimeout`, y el cierre no lo espera.
// ============================================================================

/** Cuánto dura cada sentido. Abrir se saborea; cerrar se quita de en medio. */
export const DUR_ABRIR = 500;
export const DUR_CERRAR = 300;

/** Cuadros con que se muestrea la curva. 40 a 60 fps ⇒ uno por fotograma. */
const PASOS = 40;
/** `rounded-xl` de la tarjeta del embudo (`Embudo.tsx`): de ahí sale el recorte. */
const RADIO_TARJETA = 12;

/**
 * ⭐ LA ÚNICA CONSTANTE DEL GESTO: `w₀ · duración` del muelle, en tiempo
 * normalizado. La calibró el constructor CONTRA V-227 (no de memoria), y con
 * ella la curva mide: p=0,15 ⇒ 0,018 · p=0,50 ⇒ **0,552** · p=0,90 ⇒ **0,984**.
 *
 * ⚠️ RECALIBRADA EN LA RONDA 2 (🟡 del fiscal). Estaba en 3,75, que daba 0,6295 a
 *    media duración contra un techo de vara de 0,65: **0,02 de margen**, o sea
 *    que cualquier retoque al alza la ponía roja y nadie sabría por qué. No se
 *    movió el techo —el techo es del plan y describe el daño («que el gesto no se
 *    agote en el primer tercio»)—: se centró la calibración dentro de él.
 *    Con 3,0 el margen es 0,10 por arriba y 0,15 por abajo.
 * ⛔ Subirla vuelve al golpe seco de antes; bajarla deja el gesto sin llegar (el
 *    avance al 90 % cae por debajo del 0,97 que exige V-227). No se toca «a ojo»:
 *    se mueve el número, se corre V-227 y se mira.
 */
const TENSION = 3.0;

/** Cuánto se pasa el panel ENTERO al aterrizar, en píxeles. El plan pide 6-8. */
const REBOTE_PX = 7;

/** ¿El sistema pidió no moverse? Falla hacia «sí, no muevas nada»: si no hay
 *  `matchMedia` (jsdom viejo, un anfitrión raro) no se anima. */
function sinMovimiento(): boolean {
  try {
    return typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : true;
  } catch {
    return true;
  }
}

/**
 * Posición (0 → 1) de un muelle CRÍTICO cuando ha corrido `x = w₀·t`.
 * Es la misma física que usan los muelles de SwiftUI: por eso se siente Apple y
 * no «CSS con un cubic-bezier raro». Crítico = llega y se queda quieto, sin
 * pasarse: el rebote de este gesto vive en otro sitio (ver la cabecera).
 */
function resorte(x: number): number {
  if (x <= 0) return 0;
  return 1 - (1 + x) * Math.exp(-x);
}

/** Suavizado clásico en los dos extremos (arranca y termina sin tirón). */
const suavizar = (x: number) => {
  const c = Math.min(1, Math.max(0, x));
  return c * c * (3 - 2 * c);
};

const NORMA = resorte(TENSION);

/**
 * ⭐ LA CURVA DEL GESTO. `p` es 0 → 1 del TIEMPO; devuelve 0 → 1 del RECORRIDO.
 * Es una sola, la misma para los cuatro bordes y para los dos sentidos: eso es
 * lo que pidió Cristhian con «suave y simétrico». Monótona y sin rebote.
 */
export function curvaGenio(p: number): number {
  if (p <= 0) return 0;
  if (p >= 1) return 1;
  return resorte(TENSION * suavizar(p)) / NORMA;
}

/**
 * El rebote COMO BLOQUE, en píxeles de `translateX`. Sólo al abrir: en el
 * lenguaje de Apple la llegada rebota, la salida jamás.
 * Vale 0 hasta el 70 % del tiempo, se pasa `REBOTE_PX` a mitad del último tramo
 * y vuelve EXACTO a 0 en el último cuadro (`sin²` ⇒ derivada nula en los dos
 * extremos: entra y sale sin tirón).
 */
function reboteBloque(p: number): number {
  if (p <= 0.7) return 0;
  const q = Math.min(1, (p - 0.7) / 0.3);
  const s = Math.sin(Math.PI * q);
  return REBOTE_PX * s * s;
}

const mez = (a: number, b: number, e: number) => a + (b - a) * e;

/** Una forma del viaje: el rectángulo y el radio de sus dos lados. */
interface Forma { r: DOMRect; rIzq: number; rDer: number }

/** El `transform` que lleva la lámina al rectángulo que toca en ese instante.
 *
 *  ⭐ T-LAT-7: UN SOLO avance `e` para los cuatro bordes (V-226): los cuatro
 *  bordes van al mismo paso, sin el estirón del «aladino».
 *
 *  ⭐⭐ FLUIDEZ (2-nov-2026): ANTES ERA UN `clip-path`, AHORA ES UN `transform`.
 *  Cristhian, ya con el panel rápido: «se nota cuando se va abriendo la tarjeta…
 *  fotograma a fotograma… que se vea supersuave, como en macOS». Medido con
 *  `tools/banco-genio.mjs`: el `clip-path` se recalcula en el HILO PRINCIPAL y
 *  obliga a REPINTAR la capa en cada cuadro (~200 repintadas por apertura) —
 *  justo mientras el hilo del chat se está pintando encima. Un `transform` lo
 *  mueve la GPU sin repintar nada: sigue liso aunque el hilo principal esté
 *  ocupado. Es lo que hace macOS: mueve una imagen, no la redibuja.
 *  ⚠️ Se ESCALA la lámina, y no contradice la prohibición de abajo: la lámina
 *     está VACÍA (ni una letra), así que no hay nada que deformar. Lo que no se
 *     escala jamás es el PANEL (V-235).
 *  `base` es la caja donde vive la lámina (la del panel); `transform-origin` 0 0.
 *  ⚠️ x/y/width/height y no left/right: un rectángulo armado a mano (un doble de
 *     prueba) suele traer solo esos cuatro, y con los otros saldría `NaN`. */
function forma(a: Forma, b: Forma, base: { x: number; y: number; width: number; height: number }, e: number): string {
  const izq = mez(a.r.x, b.r.x, e);
  const arr = mez(a.r.y, b.r.y, e);
  const ancho = mez(a.r.width, b.r.width, e);
  const alto = mez(a.r.height, b.r.height, e);
  return `translate(${(izq - base.x).toFixed(3)}px, ${(arr - base.y).toFixed(3)}px)`
    + ` scale(${(ancho / base.width).toFixed(5)}, ${(alto / base.height).toFixed(5)})`;
}

/** La lámina que hace el viaje: del color del panel, sin una sola letra dentro,
 *  puesta EN EL SITIO DEL PANEL y llevada a la tarjeta con `transform`.
 *  ⭐ T-LAT-7 · arranca vestida de tarjeta: el hilo de borde se LEE de la tarjeta
 *  (`getComputedStyle`), no se copia a mano. Sin tarjeta cae a un gris neutro.
 *  ⭐ FLUIDEZ (2-nov): la sombra va FIJA (`box-shadow`) y viaja con la lámina; ya
 *  no se anima un `filter: drop-shadow` sobre una capa del tamaño de la ventana,
 *  que era la operación más cara del gesto. */
function lamina(el: HTMLElement, base: DOMRect, radio: number, tarjeta?: HTMLElement | null): HTMLElement | null {
  const padre = el.parentElement;
  if (!padre || typeof document === "undefined") return null;
  const d = document.createElement("div");
  const hayEstilo = typeof getComputedStyle === "function";
  const est = hayEstilo ? getComputedStyle(el) : null;
  const estT = hayEstilo && tarjeta ? getComputedStyle(tarjeta) : null;
  d.setAttribute("aria-hidden", "true");
  d.dataset.genio = "lamina";
  d.style.cssText = `position:fixed;left:${base.x}px;top:${base.y}px;width:${base.width}px;height:${base.height}px;`
    + "z-index:2;pointer-events:none;transform-origin:0 0;will-change:transform,opacity;"
    + `border-radius:${radio}px 0 0 ${radio}px`;
  d.style.background = est?.backgroundColor && est.backgroundColor !== "rgba(0, 0, 0, 0)"
    ? est.backgroundColor
    : "var(--color-superficie)";
  const borde = estT?.borderTopColor && estT.borderTopColor !== "rgba(0, 0, 0, 0)"
    ? estT.borderTopColor
    : "rgba(0, 0, 0, 0.12)";
  d.style.boxShadow = `0 0 0 1px ${borde}, var(--shadow-flotante)`;
  padre.appendChild(d);
  return d;
}

/**
 * Estira el panel `el` desde el rectángulo `desde` (la tarjeta que se pulsó)
 * hasta su sitio, o al revés para cerrar.
 *
 * `tarjeta` es el ELEMENTO de esa tarjeta, si sigue viva: de ella se leen el
 * borde y la sombra con que arranca la lámina, y es la que da el temblorcito de
 * T-LAT-7. **Es opcional y falla en silencio**: al cerrar la tarjeta puede
 * haberse filtrado, movido de etapa o salido de la vista, y el cierre tiene que
 * seguir igual de bien sin ella.
 *
 * Devuelve una promesa que resuelve cuando termina — y de inmediato cuando no
 * hay nada que animar. **Nunca rechaza.**
 */
export function animarGenio(
  el: HTMLElement,
  desde: DOMRect | null,
  sentido: "abrir" | "cerrar",
  tarjeta?: HTMLElement | null,
): Promise<void> {
  if (!desde || typeof (el as any).animate !== "function" || sinMovimiento()) {
    return Promise.resolve();
  }
  const pan = el.getBoundingClientRect();
  // Sin tamaño (aún no pintado, o `display:none`) no hay recorte que valga.
  if (!pan.width || !pan.height) return Promise.resolve();

  const abre = sentido === "abrir";
  const dur = abre ? DUR_ABRIR : DUR_CERRAR;
  const est = typeof getComputedStyle === "function" ? getComputedStyle(el) : null;
  const radioPanel = parseFloat(est?.borderTopLeftRadius || "") || 16;

  const tarj: Forma = { r: desde, rIzq: RADIO_TARJETA, rDer: RADIO_TARJETA };
  const panel: Forma = { r: pan, rIzq: radioPanel, rDer: 0 };
  const [a, b] = abre ? [tarj, panel] : [panel, tarj];

  const avances: number[] = [];
  const cuadrosLamina: Keyframe[] = [];
  for (let i = 0; i <= PASOS; i++) {
    const p = i / PASOS;                       // 0 → 1 del tiempo
    // El último cuadro se clava en el destino exacto: la cola de la curva es
    // imperceptible pero dejaría el recorte a medio píxel de su sitio.
    const e = i === PASOS ? 1 : curvaGenio(p);
    avances.push(e);
    // El rebote va DELANTE (en pantalla), la forma detrás: así los 7 px son
    // 7 px de verdad y no 7 px escalados.
    const t = abre ? `translateX(${reboteBloque(p).toFixed(3)}px) ` : "";
    cuadrosLamina.push({ offset: p, transform: t + forma(a, b, pan, e) });
  }

  const capa = lamina(el, pan, radioPanel, tarjeta);
  const puedeCapa = !!capa && typeof (capa as any).animate === "function";
  // ⭐ T-LAT-7 · AL CERRAR, LA LÁMINA SE APAGA ANTES DE ATERRIZAR (V-234).
  // 🔴 EL DEFECTO QUE ESTO CIERRA, y lo vio Cristhian: «al llegar la tarjeta a su
  // ubicación original, sobre el nombre de la tarjeta se ve llegar un recuadro
  // gris que tapa por un momento el nombre». La causa era que el fundido estaba
  // puesto SOLO al abrir: al cerrar la lámina aterrizaba a opacidad 1 y se
  // retiraba de golpe en el turno siguiente, así que durante unos fotogramas
  // había una plancha del color del panel justo encima del nombre.
  // ⛔ Se apaga LA LÁMINA, no el panel: el panel sigue invisible hasta que el
  //    llamador lo desmonte, o parpadearía de vuelta a la vista.
  if (!abre) {
    for (let i = 0; i <= PASOS; i++) {
      cuadrosLamina[i].opacity = 1 - suavizar((avances[i] - 0.45) / 0.4);
    }
  }
  const animCapa = puedeCapa
    ? (capa as any).animate(cuadrosLamina, { duration: dur, easing: "linear", fill: "both" })
    : null;
  // La lámina no APARECE de golpe sobre la tarjeta: se funde con ella en 90 ms.
  // Sin esto, el primer fotograma borra el nombre y el teléfono de la tarjeta y
  // se lee como un parpadeo — visto en el laboratorio, en un navegador de verdad.
  if (puedeCapa && abre) {
    (capa as any).animate([{ opacity: 0 }, { opacity: 1 }],
      { duration: 90, easing: "linear", fill: "both" });
  }

  // El panel de verdad NO SE ESCALA ni se recorta: solo se FUNDE (y rebota como
  // bloque). Opacidad y `translateX` los mueve la GPU, sin repintar el hilo.
  // ⭐ FLUIDEZ (2-nov): antes iba recortado por la misma forma que la lámina, y
  //    ese recorte obligaba a repintar el chat ENTERO en cada cuadro. Ya no hace
  //    falta: el contenido no asoma hasta que la FORMA pasó del 88 % (V-229 pide
  //    ≥ 80 %), así que se revela cuando la lámina ya casi lo cubre todo.
  // ⭐ T-LAT-7 · la rampa se mide contra la FORMA, no contra el reloj: primero
  //    llega el continente, después lo de dentro.
  const rampa = (e: number, p: number) => (abre
    ? suavizar((e - 0.88) / 0.12)                    // entra cuando la forma llegó
    : 1 - Math.min(1, Math.max(0, p / 0.4)));        // y se va pronto al cerrar
  const cuadrosPanel: Keyframe[] = avances.map((e, i) => ({
    offset: i / PASOS,
    opacity: rampa(e, i / PASOS),
    // ⭐ EL REBOTE COMO BLOQUE: el MISMO `translateX` que la lámina, para que
    // todo el material llegue y se asiente junto. `translateX` no deforma el
    // texto; `scale` sí — y eso fue lo que hundió la primera versión.
    transform: abre ? `translateX(${reboteBloque(i / PASOS).toFixed(3)}px)` : "none",
  }));
  const zAntes = el.style.zIndex, wcAntes = el.style.willChange, trAntes = el.style.transform;
  el.style.zIndex = "3";
  el.style.willChange = "opacity, transform";
  const animPanel = (el as any).animate(cuadrosPanel, {
    duration: dur, easing: "linear", fill: "both",   // la curva ya va en los cuadros
  });

  // El fondo oscuro acompaña, y también al cerrar: antes desaparecía de golpe.
  // La X entra la ÚLTIMA, cuando el panel ya está: un botón flotando sobre un
  // hueco que todavía se está abriendo es de las cosas que se ven baratas.
  const acompana = (sel: string, cuadros: Keyframe[], opciones: KeyframeAnimationOptions) => {
    const n = el.parentElement?.querySelector<HTMLElement>(sel);
    if (n && typeof (n as any).animate === "function") (n as any).animate(cuadros, opciones);
  };
  acompana('[data-genio="fondo"]',
    abre ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 1 }, { opacity: 0 }],
    { duration: abre ? 240 : dur, easing: "cubic-bezier(0.33, 0, 0.2, 1)", fill: "both" });
  acompana('[data-genio="x"]',
    abre
      ? [{ opacity: 0, transform: "scale(0.8)" }, { opacity: 1, transform: "none" }]
      : [{ opacity: 1 }, { opacity: 0 }],
    abre
      ? { duration: 220, delay: 300, easing: "cubic-bezier(0.2, 1.1, 0.4, 1)", fill: "both" }
      : { duration: 120, easing: "linear", fill: "both" });

  // ── T-LAT-7 · LA TARJETA ACUSA RECIBO DEL GESTO ───────────────────────────
  // Lo pidió Cristhian: «que la tarjeta también tenga movimiento: un pequeño
  // temblorcito, un pequeño rebote de profundidad… sutil, amistoso». Al ABRIR se
  // hunde («entregué lo que tenía»); al CERRAR rebota por encima de 1 («lo
  // recibí»), y va DESPUÉS de que la lámina se haya apagado, o el temblor
  // ocurriría debajo del tapón gris y no se vería.
  // ⛔⛔ AQUÍ SÍ SE ESCALA, Y ES LA ÚNICA EXCEPCIÓN DEL ARCHIVO. La tarjeta es un
  //    elemento pequeño, el movimiento dura ~220 ms y la escala no pasa del ±5 %:
  //    la deformación del texto es imperceptible. **Sobre el PANEL sigue
  //    prohibido escalar** — eso fue lo que hundió la primera versión (letras
  //    aplastadas diez veces). Que quede escrito, o alguien lo «unificará».
  // ⚠️ NO BLOQUEA y FALLA EN SILENCIO: sin tarjeta viva no hay temblor y el
  //    cierre sigue exactamente igual (V-236).
  const temblarTarjeta = () => {
    if (!tarjeta || typeof (tarjeta as any).animate !== "function") return;
    const cuadros: Keyframe[] = abre
      ? [{ transform: "none" }, { transform: "scale(0.965)", offset: 0.45 }, { transform: "none" }]
      : [{ transform: "none" }, { transform: "scale(1.035)", offset: 0.42 }, { transform: "none" }];
    // ⛔ SIN `fill`: al terminar, el elemento vuelve solo a su `transform` de
    //    siempre. Con `fill: both` se quedaría la escala PEGADA a la tarjeta.
    // ⛔⛔ NO SE ESCRIBE `tarjeta.style.transform` NI PARA «RESTAURAR» (🟠 H-2 del
    //    fiscal, ronda 2). Aquí había un `Promise.resolve(anim.finished).then(() =>
    //    tarjeta.style.transform = antes)` que se vendía como cinturón y era el
    //    daño: esta función NUNCA asigna `style.transform`, así que no hay nada
    //    que restaurar — lo único que hacía ese `then` era, 220 ms después,
    //    **PISAR** con el valor viejo cualquier `transform` en línea que hubiera
    //    puesto otro entretanto sobre esa tarjeta. Y sobra: sin `fill`, la Web
    //    Animations API devuelve el elemento a su estilo de siempre ella sola.
    (tarjeta as any).animate(cuadros,
      { duration: 220, easing: "cubic-bezier(0.33, 0, 0.2, 1)" });
  };
  // ⚠️ Al cerrar va con retraso A PROPÓSITO: la lámina tiene que haberse apagado
  //    ya (V-234) o el temblor ocurriría debajo del tapón gris y no se vería.
  // 🔴 H-2 DEL FISCAL (ronda 2, 2-sep-2026): AQUÍ HABÍA UN COMENTARIO QUE
  // PROMETÍA ESTA CANCELACIÓN Y NO EXISTÍA — `grep clearTimeout` daba cero.
  // El escenario que abría: el asesor cierra el chat de Ana (300 ms ⇒ el temblor
  // queda agendado para los 186) y a los 100 ms pulsa la tarjeta de Pedro. A los
  // 186 ms el temblor huérfano hace `scale(1.035)` sobre la tarjeta de ANA en
  // mitad del gesto de Pedro, y 220 ms después **pisa el `style.transform`** de
  // esa tarjeta con el valor que leyó, borrando lo que hubiera puesto nadie.
  // Un comentario que promete un candado inexistente es peor que no tener el
  // candado. Ahora hay TRES candados de verdad, y ninguno promete más de lo que
  // hace — los tres los mide V-236:
  //   (1) esta función **ya no escribe `style.transform`** (arriba), que era la
  //       mitad del daño: el pisotón 220 ms después;
  //   (2) **un temblor pendiente por tarjeta**: al empezar CUALQUIER gesto sobre
  //       esa tarjeta —abrir o cerrar— se cancela el que tuviera esperando, así
  //       que abrir y cerrar deprisa sobre una tarjeta no le encola dos brincos.
  //       ⚠️ La cancelación va ANTES del `if (abre)` y no dentro de la rama de
  //       cierre, y es N-2 del fiscal (ronda 2, MEDIDO): estando sólo en la rama
  //       de cierre, `cerrar→cerrar` daba 1 temblor pero **`cerrar→ABRIR` daba 2**
  //       —el gesto más natural que hay: te equivocas de X y vuelves a pulsar la
  //       misma tarjeta— y el huérfano caía a los 186 ms EN MITAD del gesto de
  //       apertura: la tarjeta se encoge y acto seguido pega un brinco hacia
  //       fuera mientras el panel está naciendo de ella. El comentario decía
  //       cubrirlo y cubría la mitad.
  // ⛔ Y SON DOS, NO TRES. La primera versión de este arreglo metía además un
  //    `clearTimeout` en `soltarEstilos()` «por cinturón», y **mataba la
  //    función**: cuando `finished` resuelve pronto (una animación cancelada, un
  //    doble de prueba), `alTerminar` corría antes de la hora del temblor y lo
  //    cancelaba — el cierre se quedaba sin su «lo recibí». Lo cazó V-236 en el
  //    primer intento. Un candado que apaga lo que venía a proteger no es un
  //    candado: es el defecto con otro nombre.
  // ⛔ Primero se cancela lo que hubiera, PASE LO QUE PASE con el sentido nuevo.
  if (tarjeta) {
    const pendiente = (tarjeta as any).__genioTemblor;
    if (pendiente) { clearTimeout(pendiente); (tarjeta as any).__genioTemblor = null; }
  }
  if (abre) temblarTarjeta();
  else if (tarjeta) {
    const reloj = setTimeout(() => {
      if ((tarjeta as any).__genioTemblor === reloj) (tarjeta as any).__genioTemblor = null;
      temblarTarjeta();
    }, Math.round(dur * 0.62));
    (tarjeta as any).__genioTemblor = reloj;
  }

  /** Los estilos en línea que se pusieron para el viaje, sueltos siempre —
   *  cierre o apertura.
   *  ⚠️ NO toca el temblor pendiente: al cerrar, ese temblor ES la función («lo
   *  recibí»). Quien lo cancela es el gesto SIGUIENTE sobre la misma tarjeta.
   *  ⚪ H-8 DEL FISCAL: al cerrar, `limpiar()` no corre a propósito (el panel debe
   *  seguir invisible hasta que el llamador lo desmonte), y con eso se quedaban
   *  `zIndex:3` y `willChange` PEGADOS al panel — y desde T-LAT-7 también se
   *  quedaría el `transform` si mañana el rebote se aplicara al cerrar. Hoy no se
   *  observa porque `PanelContacto` desmonta; se cierra igual, que es barato.
   *  ⛔ Lo que NO se hace aquí es cancelar `animPanel`: eso devolvería la opacidad
   *  y el panel parpadearía de vuelta a la vista un fotograma. */
  const soltarEstilos = () => {
    el.style.zIndex = zAntes;
    el.style.willChange = wcAntes;
    el.style.transform = trAntes;
  };

  const limpiar = () => {
    // ⚠️ `fill: both` deja el último cuadro PEGADO al elemento. Sobre el panel
    // eso significaría quedarse con la opacidad clavada y con el `translateX`
    // del rebote; sobre la lámina, con un recorte que también recortaría su
    // sombra para siempre. Se cancela y se retira la lámina: el panel vuelve a
    // ser un elemento normal.
    try { animPanel?.cancel(); } catch { /* ya terminada */ }
    soltarEstilos();
    try { animCapa?.cancel(); } catch { /* ya terminada */ }
    capa?.remove();
  };



  // Al CERRAR no se limpia aquí: el panel tiene que quedarse invisible hasta que
  // el llamador lo desmonte, o parpadearía de vuelta a la vista un fotograma. La
  // lámina se retira en el turno siguiente, cuando React ya se llevó el diálogo
  // entero (y si no lo hizo, esto evita dejarla huérfana encima de la tarjeta).
  let terminado = false;
  const alTerminar = () => {
    if (terminado) return;
    terminado = true;
    if (abre) limpiar();
    else setTimeout(() => { capa?.remove(); soltarEstilos(); }, 0);
  };

  // ⛔⛔ EL SEGURO DE TIEMPO, Y NO ES CINTURÓN DE MÁS (medido el 2-sep-2026 en un
  // navegador de verdad): **con la pestaña en segundo plano el navegador no
  // pinta, y `finished` NO RESUELVE JAMÁS** — la animación llega a
  // `playState: "finished"` y la promesa se queda colgada hasta que alguien
  // vuelve a mirar. De esa promesa cuelga el `onCerrar()` del panel: pulsar la X
  // y cambiar de pestaña dejaba el chat abierto para siempre. Es justo lo que
  // prohíbe I-10 (b), así que la promesa se cierra por tiempo pase lo que pase.
  // `Promise.resolve` traga además un `finished` ausente (un doble de prueba, un
  // navegador viejo) y el `catch` traga la cancelación.
  // ⚠️ El temblor de la tarjeta NO entra en esta carrera: es un adorno y el
  //    cierre no lo espera. Y su `setTimeout` **no se cancela aquí**: lo cancela
  //    el gesto siguiente sobre la MISMA tarjeta (`__genioTemblor`), que es el
  //    único momento en que estorba. Cancelarlo al terminar el gesto apagaría el
  //    «lo recibí» — probado, y V-236 lo puso rojo.
  const porAnimacion = Promise.resolve(animPanel?.finished).then(() => {}, () => {});
  const porTiempo = new Promise<void>((r) => { setTimeout(r, dur + 80); });
  return Promise.race([porAnimacion, porTiempo]).then(alTerminar, alTerminar);
}
